(() => {
  const version = '2026-10-27-v4';
  if (window.bulloraLandingLoaded === version) return;
  window.bulloraLandingLoaded = version;
  const mountMotion = (root, reduced) => {
    if (reduced.matches || !('IntersectionObserver' in window) || !Element.prototype.animate) return () => {};
    const records = new Map();
    const easing = 'cubic-bezier(.22,1,.36,1)';
    const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter('hu', { granularity: 'grapheme' }) : null;
    let observer;
    let resizeTimer;
    let bootTimer;
    let disposed = false;
    let width = innerWidth;
    let height = innerHeight;
    const span = className => {
      const node = document.createElement('span');
      node.className = className;
      return node;
    };
    const restore = record => {
      if (record.original !== undefined) {
        record.node.innerHTML = record.original;
        record.original = undefined;
      }
    };
    const finish = (record, unwrap = false) => {
      clearTimeout(record.timer);
      record.node.dataset.wlMotion = 'done';
      record.animations.forEach(animation => animation.cancel());
      record.animations.clear();
      if (unwrap || record.kind === 'lines') restore(record);
    };
    const split = record => {
      const node = record.node;
      const readable = span('wl-motion-readable');
      const visual = span('wl-motion-visual');
      readable.textContent = node.innerText.replace(/\s+/g, ' ').trim();
      visual.setAttribute('aria-hidden', 'true');
      record.original = node.innerHTML;
      if (record.kind === 'chars') {
        visual.innerHTML = record.original;
        const walker = document.createTreeWalker(visual, NodeFilter.SHOW_TEXT);
        const texts = [];
        while (walker.nextNode()) texts.push(walker.currentNode);
        texts.forEach(text => {
          const fragment = document.createDocumentFragment();
          text.textContent.split(/(\s+)/).forEach(word => {
            if (!word || /^\s+$/.test(word)) {
              fragment.append(document.createTextNode(word));
              return;
            }
            const wrapper = span('wl-motion-word');
            const letters = segmenter ? [...segmenter.segment(word)].map(item => item.segment) : Array.from(word);
            letters.forEach(letter => {
              const character = span('wl-motion-char');
              character.textContent = letter;
              wrapper.append(character);
            });
            fragment.append(wrapper);
          });
          text.replaceWith(fragment);
        });
        node.replaceChildren(readable, visual);
        return [...visual.querySelectorAll('.wl-motion-char')];
      }
      const text = node.firstChild;
      if (!text || text.nodeType !== Node.TEXT_NODE || node.childNodes.length !== 1) return [];
      const range = document.createRange();
      const lines = [];
      for (const word of text.textContent.matchAll(/\S+/g)) {
        range.setStart(text, word.index);
        range.setEnd(text, word.index + word[0].length);
        const box = range.getBoundingClientRect();
        const last = lines[lines.length - 1];
        if (!last || Math.abs(last.top - box.top) > 3) lines.push({ top: box.top, words: [word[0]] });
        else last.words.push(word[0]);
      }
      lines.forEach(line => {
        const mask = span('wl-motion-line');
        const inner = span('wl-motion-line-inner');
        inner.textContent = line.words.join(' ');
        mask.append(inner);
        visual.append(mask);
      });
      node.replaceChildren(readable, visual);
      return [...visual.querySelectorAll('.wl-motion-line-inner')];
    };
    const play = record => {
      if (disposed || record.node.dataset.wlMotion !== 'waiting' || document.visibilityState === 'hidden') return;
      observer?.unobserve(record.node);
      if (reduced.matches) {
        finish(record, true);
        return;
      }
      try {
        const { node, kind, direction } = record;
        const units = kind === 'chars' || kind === 'lines' ? split(record) : [node];
        node.dataset.wlMotion = 'running';
        const distance = Math.min(innerWidth * .22, 100);
        const jobs = [];
        units.forEach((unit, index) => {
          let from;
          let to = { opacity: 1, transform: 'translate3d(0,0,0) rotate(0deg) scale(1)' };
          let duration = 1000;
          let delay = record.delay;
          if (kind === 'chars') {
            const x = direction === 'left' ? '-.35em' : direction === 'right' ? '.35em' : '0';
            from = { opacity: 0, transform: `translate3d(${x},.85em,0) rotate(${direction === 'right' ? 4 : -4}deg)` };
            duration = 880;
            delay += index * Math.min(23, 460 / Math.max(units.length - 1, 1));
          } else if (kind === 'lines') {
            from = { opacity: 0, transform: `translate3d(${direction === 'right' ? '16px' : '0'},110%,0)` };
            duration = 850;
            delay += index * 100;
          } else if (kind === 'image') {
            from = { opacity: 0, transform: `translate3d(${direction === 'left' ? -distance : distance}px,20px,0) scale(1.06)` };
            duration = 1200;
          } else if (kind === 'hero-image') {
            from = { transform: 'scale(1.045)' };
            to = { transform: 'scale(1)' };
            duration = 1800;
          } else if (kind === 'seal') {
            from = { opacity: 0, transform: 'translate3d(0,28px,0) rotate(-12deg) scale(.88)' };
            duration = 1100;
          } else {
            const x = direction === 'left' ? -distance : direction === 'right' ? distance : 0;
            from = { opacity: 0, transform: `translate3d(${x}px,${x ? 10 : direction === 'down' ? -24 : 30}px,0)` };
            duration = 900;
          }
          const animation = unit.animate([from, to], { duration, delay, easing, fill: 'both' });
          record.animations.add(animation);
          jobs.push(animation.finished);
        });
        record.timer = setTimeout(() => finish(record), 2600);
        const complete = () => { if (!disposed) finish(record); };
        Promise.all(jobs).then(complete, complete);
      } catch {
        finish(record, true);
      }
    };
    const add = (node, kind, direction = 'up', delay = 0) => {
      if (!node || records.has(node) || !node.textContent.trim() && !node.querySelector('img,svg') && node.tagName !== 'IMG') return;
      if (node.closest('[data-wl-story-card]')) return;
      if (node.closest('[data-wl-order]') && !node.closest('.wlo-heading')) return;
      node.dataset.wlMotion = 'waiting';
      node.dataset.wlMotionKind = kind;
      records.set(node, { node, kind, direction, delay, animations: new Set() });
    };
    const revealAll = () => {
      observer?.disconnect();
      records.forEach(record => finish(record, true));
    };
    const observe = () => {
      observer?.disconnect();
      if (disposed || reduced.matches || document.visibilityState === 'hidden') return;
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const record = records.get(entry.target);
          if (!record || !entry.isIntersecting) return;
          const enoughHeight = entry.intersectionRect.height >= Math.min(entry.boundingClientRect.height * .2, 56);
          const enoughWidth = entry.intersectionRect.width >= Math.min(entry.boundingClientRect.width * .45, 120);
          if (enoughHeight && enoughWidth) play(record);
        });
      }, { threshold: [0, .12, .25, .5], rootMargin: `0px 0px -${Math.round(innerHeight * (innerWidth < 750 ? .2 : .14))}px 0px` });
      records.forEach(record => {
        if (record.node.dataset.wlMotion !== 'waiting') return;
        const box = record.node.getBoundingClientRect();
        if (box.bottom < 0 && box.height) finish(record, true);
        else observer.observe(record.node);
      });
    };
    const focus = event => {
      for (let node = event.target; node && node !== root; node = node.parentElement) {
        const record = records.get(node);
        if (record) { observer?.unobserve(node); finish(record, true); }
      }
    };
    const resize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const changedWidth = Math.abs(innerWidth - width) > 2;
        const changedHeight = Math.abs(innerHeight - height) > 80;
        if (!changedWidth && !changedHeight) return;
        width = innerWidth;
        height = innerHeight;
        if (changedWidth) records.forEach(record => {
          if (record.node.dataset.wlMotion !== 'waiting') finish(record, true);
        });
        observe();
      }, 140);
    };
    const suspend = () => {
      observer?.disconnect();
      records.forEach(record => {
        if (record.node.dataset.wlMotion !== 'running') return;
        clearTimeout(record.timer);
        record.animations.forEach(animation => {
          if (animation.playState === 'running') animation.pause();
        });
      });
    };
    const resume = () => {
      if (disposed) return;
      if (reduced.matches) { revealAll(); return; }
      if (document.visibilityState === 'hidden') { suspend(); return; }
      records.forEach(record => {
        if (record.node.dataset.wlMotion !== 'running') return;
        let resumed = false;
        record.animations.forEach(animation => {
          if (animation.playState !== 'paused') return;
          animation.play();
          resumed = true;
        });
        if (resumed) {
          clearTimeout(record.timer);
          record.timer = setTimeout(() => finish(record), 2600);
        }
      });
      observe();
    };
    const preference = () => { if (reduced.matches) revealAll(); };
    const pageShow = event => { if (event.persisted) resume(); };
    try {
      root.querySelectorAll('h1,h2,h3').forEach((node, index) => add(node, 'chars', ['up', 'left', 'right'][index % 3], node.closest('.wl-hero') ? 100 : 0));
      root.querySelectorAll('p').forEach(node => {
        if (node.closest('.wl-dock,.wl-faq-answer') || node.querySelector('a,button,input,[data-wl-order-price]')) return;
        add(node, node.childElementCount || node.classList.contains('wl-fine') ? 'reveal' : 'lines', 'up', node.closest('.wl-hero') ? 240 : 90);
      });
      root.querySelectorAll('.wl-kicker,.wl-number,.wl-detail-value,.wl-detail-chips,.wl-trust>*,.wl-fit-grid li,.wl-faq-list details,.wl-flow-tabs').forEach((node, index) => {
        const direction = node.matches('.wl-fit-grid li') ? index % 2 ? 'left' : 'right' : node.matches('.wl-kicker') ? 'down' : 'up';
        add(node, 'reveal', direction, node.matches('.wl-trust>*') ? index % 3 * 90 : 0);
      });
      root.querySelectorAll('.wl-problem-image-frame>img,.wl-details-photo>img,.wl-part-media,.wl-review>img,.wl-flow-stage').forEach((node, index) => add(node, 'image', index % 2 ? 'right' : 'left'));
      root.querySelectorAll('[data-wl-story-stage]').forEach(node => add(node, 'reveal', 'up'));
      add(root.querySelector('.wl-hero-image-frame>img'), 'hero-image');
      add(root.querySelector('.wl-guarantee-seal'), 'seal');
      root.addEventListener('focusin', focus);
      window.addEventListener('resize', resize, { passive: true });
      window.addEventListener('pageshow', pageShow);
      window.addEventListener('pagehide', suspend);
      document.addEventListener('visibilitychange', resume);
      document.addEventListener('prerenderingchange', resume);
      reduced.addEventListener('change', preference);
      let started = false;
      const boot = () => {
        if (disposed || started) return;
        started = true;
        clearTimeout(bootTimer);
        observe();
      };
      bootTimer = setTimeout(boot, 180);
      document.fonts?.ready.then(boot, boot);
    } catch {
      revealAll();
    }
    return () => {
      disposed = true;
      clearTimeout(bootTimer);
      clearTimeout(resizeTimer);
      revealAll();
      records.forEach(record => {
        delete record.node.dataset.wlMotion;
        delete record.node.dataset.wlMotionKind;
      });
      root.removeEventListener('focusin', focus);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pageshow', pageShow);
      window.removeEventListener('pagehide', suspend);
      document.removeEventListener('visibilitychange', resume);
      document.removeEventListener('prerenderingchange', resume);
      reduced.removeEventListener('change', preference);
    };
  };
  const init = root => {
    if (root.dataset.wlReady === version) return;
    if (root.dataset.wlReady) root.wlCleanup?.();
    root.dataset.wlReady = version;
    const cleanups = [];
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview && /^\d+$/.test(preview)) {
      root.querySelectorAll('[data-wl-build-link]').forEach(link => {
        const url = new URL(link.href, location.origin);
        if (url.origin !== location.origin) return;
        url.searchParams.set('preview_theme_id', preview);
        link.href = url.href;
      });
    }
    root.querySelectorAll('[data-wl-flow]').forEach(flow => {
      const list = flow.querySelector('[data-flow-tabs]');
      const tabs = [...flow.querySelectorAll('[data-flow-tab]')];
      const panels = [...flow.querySelectorAll('[data-flow-panel]')];
      if (!list || !tabs.length || tabs.length !== panels.length) return;
      let active = 0;
      const show = (index, focus = false) => {
        active = index;
        tabs.forEach((tab, i) => {
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', String(i === index));
          tab.tabIndex = i === index ? 0 : -1;
          panels[i].hidden = i !== index;
          panels[i].setAttribute('role', 'tabpanel');
          panels[i].setAttribute('aria-labelledby', tab.id);
          panels[i].tabIndex = 0;
        });
        if (focus) tabs[index].focus({ preventScroll: true });
      };
      const click = event => {
        const tab = event.target.closest('[data-flow-tab]');
        if (tab && list.contains(tab)) show(Number(tab.dataset.flowTab));
      };
      const keydown = event => {
        if (!event.target.matches('[data-flow-tab]')) return;
        const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (active + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        show(next, true);
      };
      const selectBlock = event => {
        const panel = event.target.closest('[data-flow-panel]');
        if (panel && flow.contains(panel)) show(Number(panel.dataset.flowPanel));
      };
      list.setAttribute('role', 'tablist');
      list.hidden = false;
      flow.querySelector('.wl-flow-panels').setAttribute('data-flow-ready', '');
      show(0);
      list.addEventListener('click', click);
      list.addEventListener('keydown', keydown);
      flow.addEventListener('shopify:block:select', selectBlock);
      cleanups.push(() => {
        list.removeEventListener('click', click);
        list.removeEventListener('keydown', keydown);
        flow.removeEventListener('shopify:block:select', selectBlock);
      });
    });
    root.querySelectorAll('[data-wl-story]').forEach(story => {
      const stage = story.querySelector('[data-wl-story-stage]');
      const cards = [...story.querySelectorAll('[data-wl-story-card]')];
      const tools = story.querySelector('[data-wl-story-tools]');
      const dots = [...story.querySelectorAll('[data-wl-story-go]')];
      const count = story.querySelector('[data-wl-story-count]');
      if (!stage || !cards.length || !tools || !count) return;
      let active = 0;
      let start;
      let suppressClickUntil = 0;
      const show = index => {
        if (!Number.isInteger(index)) return;
        active = (index + cards.length) % cards.length;
        cards.forEach((card, i) => {
          let distance = (i - active + cards.length) % cards.length;
          if (distance > Math.floor(cards.length / 2)) distance -= cards.length;
          const current = distance === 0;
          const distant = Math.abs(distance) > 1;
          card.style.setProperty('--scene-x', `${distance * 72}%`);
          card.style.setProperty('--scene-y', current ? '0px' : '30px');
          card.style.setProperty('--scene-rotation', `${distance * 10}deg`);
          card.style.setProperty('--scene-scale', current ? '1' : '.88');
          card.style.setProperty('--scene-opacity', distant ? '0' : '1');
          card.style.setProperty('--scene-depth', current ? '4' : distant ? '1' : '2');
          card.setAttribute('aria-hidden', String(!current));
          card.toggleAttribute('data-story-distant', distant);
          card.tabIndex = current ? 0 : -1;
        });
        dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === active)));
        count.textContent = `${String(active + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
        story.dataset.storyIndex = String(active);
      };
      const click = event => {
        const next = event.target.closest('[data-wl-story-next]');
        const previous = event.target.closest('[data-wl-story-prev]');
        const dot = event.target.closest('[data-wl-story-go]');
        if (next) show(active + 1);
        else if (previous) show(active - 1);
        else if (dot) show(Number(dot.dataset.wlStoryGo));
        else {
          const card = event.target.closest('[data-wl-story-card]');
          if (card && performance.now() >= suppressClickUntil) show(Number(card.dataset.wlStoryCard));
        }
      };
      const keydown = event => {
        if (event.ctrlKey || event.metaKey || event.altKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        show(event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : active + (event.key === 'ArrowRight' ? 1 : -1));
      };
      const pointerdown = event => {
        if (!event.isPrimary || event.pointerType === 'mouse' && event.button !== 0) return;
        start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      };
      const pointerup = event => {
        if (!start || start.id !== event.pointerId) return;
        const x = event.clientX - start.x;
        const y = event.clientY - start.y;
        start = undefined;
        if (Math.abs(x) < 42 || Math.abs(x) < Math.abs(y) * 1.4) return;
        suppressClickUntil = performance.now() + 350;
        show(active + (x < 0 ? 1 : -1));
      };
      const cancel = () => { start = undefined; };
      const selectBlock = event => {
        const card = event.target.closest('[data-wl-story-card]');
        if (card && story.contains(card)) show(Number(card.dataset.wlStoryCard));
      };
      show(0);
      story.setAttribute('data-story-ready', '');
      tools.hidden = cards.length < 2;
      story.addEventListener('click', click);
      story.addEventListener('keydown', keydown);
      stage.addEventListener('pointerdown', pointerdown, { passive: true });
      window.addEventListener('pointerup', pointerup, { passive: true });
      window.addEventListener('pointercancel', cancel, { passive: true });
      story.addEventListener('shopify:block:select', selectBlock);
      cleanups.push(() => {
        story.removeEventListener('click', click);
        story.removeEventListener('keydown', keydown);
        stage.removeEventListener('pointerdown', pointerdown);
        window.removeEventListener('pointerup', pointerup);
        window.removeEventListener('pointercancel', cancel);
        story.removeEventListener('shopify:block:select', selectBlock);
        story.removeAttribute('data-story-ready');
        delete story.dataset.storyIndex;
        tools.hidden = true;
        cards.forEach(card => {
          ['--scene-x','--scene-y','--scene-rotation','--scene-scale','--scene-opacity','--scene-depth'].forEach(property => card.style.removeProperty(property));
          ['aria-hidden','tabindex','data-story-distant'].forEach(attribute => card.removeAttribute(attribute));
        });
      });
    });
    cleanups.push(mountMotion(root, reduced));
    root.querySelectorAll('[data-wl-carousel]').forEach(track => {
      const tools = root.querySelector(`[data-wl-tools="${track.id}"]`);
      if (!tools) return;
      const previous = tools.querySelector('[data-wl-prev]');
      const next = tools.querySelector('[data-wl-next]');
      const count = tools.querySelector('[data-wl-index]');
      const items = [...track.children];
      let frame = 0;
      const update = () => {
        frame = 0;
        const start = track.getBoundingClientRect().left;
        let index = 0;
        let distance = Infinity;
        items.forEach((item, i) => {
          const delta = Math.abs(item.getBoundingClientRect().left - start);
          if (delta < distance) { index = i; distance = delta; }
        });
        previous.disabled = index === 0;
        next.disabled = index === items.length - 1;
        count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const move = direction => {
        const step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : track.clientWidth;
        track.scrollBy({ left: step * direction, behavior: reduced.matches ? 'instant' : 'smooth' });
      };
      const back = () => move(-1);
      const forward = () => move(1);
      previous.addEventListener('click', back);
      next.addEventListener('click', forward);
      track.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      tools.hidden = false;
      update();
      cleanups.push(() => {
        cancelAnimationFrame(frame);
        previous.removeEventListener('click', back);
        next.removeEventListener('click', forward);
        track.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
      });
    });
    const dock = root.querySelector('[data-wl-dock]');
    const heroCta = root.querySelector('[data-wl-hero-cta]');
    const finalCta = root.querySelector('[data-wl-final-cta]');
    if (dock && heroCta && finalCta) {
      let frame = 0;
      const update = () => {
        frame = 0;
        const first = heroCta.getBoundingClientRect();
        const last = finalCta.getBoundingClientRect();
        const overlay = [...document.querySelectorAll('cart-drawer.active, .menu-drawer-container[open], dialog[open], .shopify-pc__banner__dialog')].some(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
        dock.hidden = innerWidth > 749 || first.bottom > 0 || (last.top < innerHeight && last.bottom > 0) || overlay;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('click', schedule);
      update();
      cleanups.push(() => {
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        document.removeEventListener('click', schedule);
      });
    }
    root.wlCleanup = () => {
      cleanups.forEach(cleanup => cleanup());
      delete root.dataset.wlReady;
    };
  };
  const scan = (scope = document) => {
    if (window.bulloraLandingLoaded !== version) return;
    scope.querySelectorAll('[data-wl-landing]').forEach(init);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-wl-landing]').forEach(root => root.wlCleanup?.()));
})();

